import React, { PureComponent } from 'react'
import { observer } from 'mobx-react'
import { reaction } from 'mobx'
import * as R from 'ramda'
import i18n from 'i18next'
import { v4 as uuidv4 } from 'uuid'
import Modal from '../../../UniformComponents/Modal'
import ModalHeader from '../../../UniformComponents/ModalHeader'
import ModalFooter from '../../../UniformComponents/ModalFooter'
import Checkbox from '../../form/Checkbox'
import CheckboxGroup from '../../form/CheckboxGroup'
import Button from '../../../UniformComponents/Button'
import Select from '../../form/Select'
import Panel from '../../../UniformComponents/Panel'
import {
    getIndustries, getProductTypes, getSurveyTypes, getRespondentTypes,
} from '../../../utils/helpers'
import Tooltip from '../../../UniformComponents/Tooltip'
import { unescapeHtmlTags, stripOutHtml } from '../../helpers'
import ChartPreviewContainer from './ChartPreviewContainer'
import StagedForm from './StagedForm'

@observer
class WidgetCreator extends PureComponent {
    quadrantIndex = ['xAxis', 'yAxis', 'zAxis']

    constructor(props) {
        super(props)
        const { dashboardWizardStore } = this.props
        const {
            changeNewWidget, initDataSetup, getNonQuestionData,
        } = dashboardWizardStore

        this.state = {
            activePanel: 'metrics',
            openAccordian: false,
            multiIndex: 0,
        }

        const industries = this.getIndustryOptions()

        reaction(
            () => [dashboardWizardStore.dashStore.showWidgetCreator],
            async () => {
                if (
                    dashboardWizardStore.dashStore.showWidgetCreator
                    && !dashboardWizardStore.dashStore.widgetEditorKeyNew
                ) {
                    await initDataSetup()
                    await getNonQuestionData()
                    const dataLevel = R.pathOr([], ['dataLevels'], dashboardWizardStore)[0]
                    changeNewWidget(dataLevel.value, 'dataLevel')
                    const timePeriod = this.getTimePeriodOptions()[0]
                    changeNewWidget(timePeriod.value, 'timePeriod')
                    if (industries.length === 1) this.blurIndustry(industries[0])
                }
            },
        )
        reaction(
            () => [dashboardWizardStore.widgetType],
            () => {
                if (dashboardWizardStore.widgetType === 'multi') {
                    changeNewWidget('multiQuestion', 'nonQuestionType')
                    changeNewWidget('multiQuestion', 'nonQuestionKpiType')
                } else if (dashboardWizardStore.widgetType === 'quadrantAnalysis') {
                    changeNewWidget('quadrantAnalysis', 'nonQuestionType')
                    changeNewWidget('quadrantAnalysis', 'nonQuestionKpiType')
                } else {
                    changeNewWidget('', 'nonQuestionType')
                }
            },
        )
    }

    async componentDidUpdate(prevProps) {
        const { widgetKey, dashboardWizardStore } = this.props
        const {
            dashStore, changeNewWidget, initDataSetup, getNonQuestionData,
        } = dashboardWizardStore
        if (widgetKey && prevProps.widgetKey !== widgetKey) {
            changeNewWidget(true, 'loading')
            const widgets = R.pathOr([{}], ['widgets'], dashStore)
            const widget = widgets.find((w) => w.key === widgetKey)
            this.setWidgetType(widget)
            await initDataSetup()
            await getNonQuestionData()
            await this.loadExistingWidget()
        }
    }

    setWidgetType = (widget) => {
        const { dashboardWizardStore } = this.props
        const { changeWidgetType } = dashboardWizardStore
        if (widget.nonQuestionType === 'KPI') {
            changeWidgetType('aggregated')
        } else if (widget.questionId && widget.questionId !== '00000000-0000-0000-0000-000000000000') {
            changeWidgetType('single')
        } else {
            changeWidgetType('multi')
        }
    }

    loadExistingWidget = async () => {
        const { dashboardWizardStore, widgetKey } = this.props
        const {
            dashStore, changeNewWidgetBulk, getMultiQuestions, getQuestions, widgetType, changeNewWidget,
        } = dashboardWizardStore
        const widgets = R.pathOr([{}], ['widgets'], dashStore)
        const widget = widgets.find((w) => w.key === widgetKey)

        const dataLevel = R.pathOr([], ['dataLevels'], dashboardWizardStore)[0]
        changeNewWidget(dataLevel.value, 'dataLevel')
        const timePeriod = this.getTimePeriodOptions()[0]
        changeNewWidget(timePeriod.value, 'timePeriod')

        const {
            respondentType,
            productType,
            surveyType,
            industry,
            chartType,
            filter,
            widgetRenderSettings,
            scope,
            nonQuestionType,
            kpiType,
            questionTypeCode,
            questionId,
            benchmarkType,
            parentQuestion,
            width,
            multiQuestion,
            title,
            formId,
            qlistParentId,
            customTag,
            tagOptions,
            optionFilter,
            includeRegistrationData,
            isMatrixKpi,
        } = widget

        changeNewWidgetBulk([{ value: chartType, field: 'chart' }])

        const widgetValues = [
            { value: isMatrixKpi, field: 'isMatrixKpi' },
            { value: industry, field: 'industry' },
            { value: productType, field: 'productType' },
            { value: surveyType, field: 'surveyType' },
            { value: respondentType, field: 'respondentType' },
            { value: filter, field: 'filter' },
            { value: widgetRenderSettings, field: 'widgetRenderSettings' },
            { value: scope, field: 'dataLevel' },
            { value: nonQuestionType, field: 'nonQuestionType' },
            { value: questionTypeCode, field: 'questionTypeCode' },
            { value: benchmarkType, field: 'timePeriod' },
            { value: parentQuestion, field: 'parentQuestion' },
            { value: nonQuestionType || 'question', field: 'widgetBasis' },
            { value: width, field: 'defaultSize' },
            { value: multiQuestion, field: 'multiQuestion' },
            { value: title, field: 'widgetTitle' },
            { value: formId, field: 'formId' },
            { value: qlistParentId, field: 'qlistParentId' },
            { value: customTag, field: 'customTag' },
            { value: tagOptions, field: 'tagOptions' },
            { value: optionFilter, field: 'optionFilter' },
            { value: includeRegistrationData, field: 'includeRegistrationData' },
            { value: questionId, field: 'questionId' },
            { value: kpiType, field: 'displayAs' },
        ]

        if (nonQuestionType === 'KPI') {
            widgetValues.push(
                { value: kpiType, field: 'nonQuestionKpiType' },
            )
        }

        changeNewWidgetBulk(widgetValues)
        if (['multi'].includes(widgetType)) {
            await Promise.all(widget.multiQuestion.forEach((_, i) => getMultiQuestions(i, this.quadrantIndex)))
        } else if (['quadrantAnalysis'].includes(widgetType)) {
            await getMultiQuestions(0, this.quadrantIndex)
        } else {
            await getQuestions()
        }

        if (['single', 'quadrantAnalysis'].includes(widgetType)) {
            const questionOptions = this.getQuestionControlOptions()
            this.blurQuestion(questionOptions.find((q) => q.value === questionId))
            changeNewWidgetBulk([{ value: kpiType, field: 'displayAs' }])
        }

        changeNewWidget(false, 'loading')
    }

    getQuestionOptions = () => {
        const { dashboardWizardStore } = this.props
        const { newWidget, appStore, getQuestionLabel } = dashboardWizardStore
        const { question } = newWidget
        const { langcode } = appStore

        if (!question) return []
        const { questionTypeCode } = question

        const questionPath = questionTypeCode.includes('TB')
            ? ['optionSetX', 'options']
            : ['optionSet', 'options']

        const options = R.pathOr([], questionPath, question)

        return options.map((o) => ({
            value: R.pathOr('', ['id'], o),
            label: unescapeHtmlTags(stripOutHtml(getQuestionLabel(
                question,
                langcode,
                R.pathOr({}, ['langs'], o),
                false,
            ))),
        }))
    }

    widgetScopeControls = () => {
        const { dashboardWizardStore } = this.props
        const { openAccordian } = this.state
        const { changeNewWidget } = dashboardWizardStore
        return [
            {
                name: 'optionsAccordion',
                title: i18n.t('dynamicDash.wizard.optionalSettings'),
                type: 'accordion',
                isOpen: openAccordian,
                toggleOpen: () => this.setState({ openAccordian: !openAccordian }),
                show: (store) => {
                    if (!store.newWidget.dataLevel) return 0
                    return 2
                },
                id: 1,
            },
            {
                name: 'optionFilter',
                type: 'select',
                label: i18n.t('dynamicDash.wizard.optionFiltersLabel'),
                tooltipText: i18n.t('dynamicDash.wizard.optionFiltersTooltip'),
                options: () => this.getQuestionOptions(),
                onChange: (v) => {
                    changeNewWidget(v.value, 'optionFilter')
                },
                isMandatory: false,
                dontAutoSet: true,
                isMulti: true,
                show: (store) => {
                    if (store.newWidget.displayAs !== 'pct') return 0
                    return 2
                },
                accordionId: 1,
                showNumberSelected: true,
                menuPortalTarget: document.body,
                styles: {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                },
            },
            {
                name: 'filter',
                type: 'treeViewSelect',
                label: i18n.t('dynamicDash.wizard.optionalFiltersLabel'),
                tooltipText: i18n.t('dynamicDash.wizard.optionalFiltersTooltip'),
                options: (store) => {
                    store.createTreeData(
                        R.pathOr([], ['newWidget', 'dataLevel'], store),
                        R.pathOr([], ['newWidget', 'filter'], store),
                    )

                    return store.filterTree
                },
                onChange: (v) => { changeNewWidget(v.value, 'filter') },
                show: (store) => {
                    if (store.newWidget.dataLevel === 'client') return 0
                    if (store.dashStore.oId === 6) return 0
                    if (!store.newWidget.dataLevel) return 0
                    return 2
                },
                validate: (({ value }) => !!value.length),
                accordionId: 1,
            },
            process.env.USE_CUSTOM_BENCHMARKS === 'true' ? {
                name: 'customTag',
                type: 'select',
                label: i18n.t('dynamicDash.wizard.groupByLabel'),
                tooltipText: i18n.t('dynamicDash.wizard.groupByTooltip'),
                options: (store) => [
                    { label: 'None', value: 0, tagOptions: [] },
                    ...store.benchmarkTags.map((b) => (
                        {
                            value: b.customTagId,
                            label: b.parentTagName || b.customTagName,
                            tagOptions: b.tagOptions,
                        }
                    )),
                ],
                onChange: (v) => {
                    changeNewWidget(v.value || undefined, 'customTag')
                    changeNewWidget(v.tagOptions.map((b) => {
                        const tagOptionConfig = JSON.parse(b.tagOptionConfig)
                        const label = tagOptionConfig.name || tagOptionConfig.langs.en_GB
                        return (
                            { value: b.tagOptionId, label }
                        )
                    }),
                    'tagOptions')
                },
                dontAutoSet: true,
                isMandatory: false,
                show: (store) => {
                    if (!store.benchmarkTags.length) return 0
                    if (store.dashStore.oId === 6) return 0
                    if (!store.newWidget.dataLevel) return 0
                    return 2
                },
                accordionId: 1,
                menuPortalTarget: document.body,
                styles: {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                },
            } : undefined,
            process.env.USE_CUSTOM_BENCHMARKS === 'true' ? {
                name: 'tagOptions',
                type: 'select',
                label: i18n.t('dynamicDash.wizard.customFiltersLabel'),
                tooltipText: i18n.t('dynamicDash.wizard.customFiltersTooltip'),
                options: (store) => {
                    const { langcode } = store.appStore
                    const customTag = store.benchmarkTags
                        .find((b) => b.customTagId === R.pathOr(undefined, ['customTag'], store.newWidget))
                    return R.pathOr([], ['tagOptions'], customTag)
                        .map((b) => {
                            const parsedTagConfig = JSON.parse(b.tagOptionConfig)
                            const tagName = R.pathOr(
                                R.pathOr(
                                    R.pathOr('', ['langs', 'en_GB'], parsedTagConfig),
                                    ['langs', langcode],
                                    parsedTagConfig,
                                ),
                                ['name'], parsedTagConfig,
                            )
                            return { value: b.tagOptionId, label: tagName }
                        })
                },
                isMulti: true,
                onChange: (v) => {
                    changeNewWidget(v.value, 'tagOptions')
                },
                dontAutoSet: true,
                isMandatory: false,
                show: (store) => {
                    if (!store.newWidget.customTag) return 0
                    if (!store.benchmarkTags.length) return 0
                    if (store.dashStore.oId === 6) return 0
                    if (!store.newWidget.dataLevel) return 0
                    return 2
                },
                accordionId: 1,
                menuPortalTarget: document.body,
                styles: {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                },
                showNumberSelected: true,
            } : undefined,
        ]
    }

    setIsNumeric = (q) => {
        const { dashboardWizardStore } = this.props
        const { changeNewWidget, dashStore } = dashboardWizardStore
        const { oId } = dashStore
        if (['I', 'NF', 'NF - row', 'FM', 'PC', 'OD', 'OD - row'].includes(q.questionTypeCode)) return changeNewWidget('numeric', 'isNumeric')
        if (['RD'].includes(q.questionTypeCode)) {
            if (q.settings.frontend.renderNPS || R.pathOr('', ['metadata', 'kpiType'], q) === 'NPS') return changeNewWidget('nps', 'isNumeric')
            return changeNewWidget(q.optionSet.hasNumericCodes ? 'numeric' : 'nonNumeric', 'isNumeric')
        }
        if (['CH', 'SB', 'OD'].includes(q.questionTypeCode)) {
            return changeNewWidget(q.optionSet.hasNumericCodes ? 'numeric' : 'nonNumeric', 'isNumeric')
        }
        if (['TB', 'TB - row'].includes(q.questionTypeCode)) return changeNewWidget(q.optionSetX.hasNumericCodes ? 'numeric' : 'nonNumeric', 'isNumeric')
        if (['SG'].includes(q.questionTypeCode)) return changeNewWidget(oId === 6 ? 'numericEvent' : 'numeric', 'isNumeric')
        if (['TX', 'TA'].includes(q.questionTypeCode)) return changeNewWidget('nonNumeric', 'isNumeric')
        return ''
    }

    setOptionFilters = () => {
        const { dashboardWizardStore } = this.props
        const { newWidget, changeNewWidget } = dashboardWizardStore
        if (newWidget.displayAs !== 'pct') {
            changeNewWidget([], 'optionFilter')
            return
        }
        changeNewWidget(this.getQuestionOptions(), 'optionFilter')
    }

    toggleCollapse = (newPanel) => {
        this.setState({ activePanel: newPanel })
    }

    newMultiQuestionValue = (newValue, index) => {
        const { dashboardWizardStore } = this.props
        const { newWidget } = dashboardWizardStore
        const { multiQuestion } = newWidget
        const newMultiQ = [...multiQuestion]
        newMultiQ[index] = newValue
        return newMultiQ
    }

    getIndustryOptions = () => {
        const { dashboardWizardStore } = this.props
        const {
            productMap,
        } = dashboardWizardStore

        return getIndustries(productMap, true)
    }

    blurIndustry = (value) => {
        const { dashboardWizardStore } = this.props
        const {
            changeNewWidgetBulk,
            widgetType,
            newWidget,
        } = dashboardWizardStore
        const { multiIndex } = this.state
        const children = Object.values(value.children)
        let newValues = []
        if (widgetType === 'multi') {
            newValues = [
                {
                    value: this.newMultiQuestionValue(
                        {
                            industry: value.value,
                            productType: '',
                            surveyType: '',
                            respondentType: '',
                            question: '',
                        },
                        multiIndex,
                    ),
                    field: 'multiQuestion',
                },
            ]
        } else if (['single', 'aggregated'].includes(widgetType)) {
            newValues = [
                { value: '', field: 'question' },
                { value: '', field: 'displayAs' },
                { value: value.value, field: 'industry' },
                { value: '', field: 'productType' },
                { value: '', field: 'surveyType' },
                { value: '', field: 'respondentType' },
            ]
        } else if (['quadrantAnalysis'].includes(widgetType)) {
            const axis = this.quadrantIndex[multiIndex]
            const { quadrant } = newWidget
            const metricObject = this.getMetricObject()
            newValues = [
                {
                    value: {
                        ...quadrant,
                        [axis]: {
                            ...metricObject,
                            question: '',
                            displayAs: '',
                            industry: value.value,
                            productType: '',
                            surveyType: '',
                            respondentType: '',
                        },
                    },
                    field: 'quadrant',
                },
            ]
        }
        changeNewWidgetBulk(newValues)
        if (children.length === 1) {
            this.blurProductType(children[0])
        }
    }

    getMetricObject = () => {
        const { dashboardWizardStore } = this.props
        const {
            newWidget,
            widgetType,
        } = dashboardWizardStore
        const { multiIndex } = this.state
        if (['single', 'aggregated'].includes(widgetType)) {
            return newWidget
        }
        if (widgetType === 'multi') {
            return newWidget.multiQuestion[multiIndex]
        }
        if (widgetType === 'quadrantAnalysis') {
            return R.pathOr({}, ['quadrant', this.quadrantIndex[multiIndex]], newWidget)
        }
        return {}
    }

    getProductTypeOptions = () => {
        const { dashboardWizardStore } = this.props
        const {
            productMap,
        } = dashboardWizardStore
        const metricObject = this.getMetricObject()
        const { industry } = metricObject

        return Object.values(R.pathOr({}, [industry, 'children'], productMap))
    }

    blurProductType = (value) => {
        const { dashboardWizardStore } = this.props
        const {
            changeNewWidgetBulk,
            widgetType,
            newWidget,
        } = dashboardWizardStore
        const { multiIndex } = this.state
        let newValues = []
        const children = Object.values(value.children)

        if (widgetType === 'multi') {
            newValues = [
                {
                    value: this.newMultiQuestionValue(
                        {
                            ...newWidget.multiQuestion[multiIndex],
                            productType: value.value,
                            surveyType: '',
                            respondentType: '',
                            question: '',
                        },
                        multiIndex,
                    ),
                    field: 'multiQuestion',
                },
            ]
        } else if (['single', 'aggregated'].includes(widgetType)) {
            newValues = [
                { value: '', field: 'question' },
                { value: value.value, field: 'productType' },
                { value: '', field: 'displayAs' },
                { value: '', field: 'surveyType' },
                { value: '', field: 'respondentType' },
            ]
        } else if (['quadrantAnalysis'].includes(widgetType)) {
            const axis = this.quadrantIndex[multiIndex]
            const { quadrant } = newWidget
            const metricObject = this.getMetricObject()
            newValues = [
                {
                    value: {
                        ...quadrant,
                        [axis]: {
                            ...metricObject,
                            question: '',
                            displayAs: '',
                            productType: value.value,
                            surveyType: '',
                            respondentType: '',
                        },
                    },
                    field: 'quadrant',
                },
            ]
        }

        changeNewWidgetBulk(newValues)
        if (children.length === 1) {
            this.blurSurveyType(children[0])
        }
    }

    getSurveyTypeOptions = () => {
        const { dashboardWizardStore } = this.props
        const {
            productMap,
        } = dashboardWizardStore
        const metricObject = this.getMetricObject()
        const { industry, productType } = metricObject

        return Object.values(R.pathOr({}, [industry, 'children', productType, 'children'], productMap))
    }

    blurSurveyType = (value) => {
        const { dashboardWizardStore } = this.props
        const {
            changeNewWidgetBulk,
            widgetType,
            newWidget,
        } = dashboardWizardStore
        const { multiIndex } = this.state
        let newValues = []
        const children = Object.values(value.children)

        if (widgetType === 'multi') {
            newValues = [
                {
                    value: this.newMultiQuestionValue(
                        {
                            ...newWidget.multiQuestion[multiIndex],
                            surveyType: value.value,
                            respondentType: '',
                            question: '',
                        },
                        multiIndex,
                    ),
                    field: 'multiQuestion',
                },
            ]
        } else if (['single', 'aggregated'].includes(widgetType)) {
            newValues = [
                { value: '', field: 'question' },
                { value: value.value, field: 'surveyType' },
                { value: '', field: 'displayAs' },
                { value: '', field: 'respondentType' },
            ]
        } else if (['quadrantAnalysis'].includes(widgetType)) {
            const axis = this.quadrantIndex[multiIndex]
            const { quadrant } = newWidget
            const metricObject = this.getMetricObject()
            newValues = [
                {
                    value: {
                        ...quadrant,
                        [axis]: {
                            ...metricObject,
                            question: '',
                            displayAs: '',
                            surveyType: value.value,
                            respondentType: '',
                        },
                    },
                    field: 'quadrant',
                },
            ]
        }

        changeNewWidgetBulk(newValues)
        if (children.length === 1) {
            this.blurRespondentTypes(children[0])
        }
    }

    getRespondentTypeOptions = () => {
        const { dashboardWizardStore } = this.props
        const {
            productMap,
        } = dashboardWizardStore
        const metricObject = this.getMetricObject()
        const { industry, productType, surveyType } = metricObject

        return Object.values(R.pathOr(
            {},
            [industry, 'children', productType, 'children', surveyType, 'children'],
            productMap,
        ))
    }

    blurRespondentTypes = async (value) => {
        const { dashboardWizardStore } = this.props
        const {
            changeNewWidgetBulk,
            widgetType,
            newWidget,
            getQuestions,
            getMultiQuestions,
        } = dashboardWizardStore
        const { multiIndex } = this.state
        let newValues = []

        if (widgetType === 'multi') {
            newValues = [
                {
                    value: this.newMultiQuestionValue(
                        {
                            ...newWidget.multiQuestion[multiIndex],
                            respondentType: value.value,
                            question: '',
                        },
                        multiIndex,
                    ),
                    field: 'multiQuestion',
                },
            ]
        } else if (['single', 'aggregated'].includes(widgetType)) {
            newValues = [
                { value: '', field: 'question' },
                { value: '', field: 'displayAs' },
                { value: value.value, field: 'respondentType' },
            ]
        } else if (['quadrantAnalysis'].includes(widgetType)) {
            const axis = this.quadrantIndex[multiIndex]
            const { quadrant } = newWidget
            const metricObject = this.getMetricObject()
            newValues = [
                {
                    value: {
                        ...quadrant,
                        [axis]: {
                            ...metricObject,
                            question: '',
                            displayAs: '',
                            respondentType: value.value,
                        },
                    },
                    field: 'quadrant',
                },
            ]
        }

        changeNewWidgetBulk(newValues)
        if (['multi', 'quadrantAnalysis'].includes(widgetType)) {
            await getMultiQuestions(multiIndex, this.quadrantIndex)
        } else {
            await getQuestions()
        }
    }

    blurQuestion = (value) => {
        const { dashboardWizardStore } = this.props
        const {
            changeNewWidgetBulk,
            widgetType,
            newWidget,
        } = dashboardWizardStore
        const { multiIndex } = this.state
        let newValues = []
        dashboardWizardStore.setDisplayAs({ name: undefined })

        if (widgetType === 'multi') {
            newValues = [
                {
                    value: this.newMultiQuestionValue(
                        {
                            ...newWidget.multiQuestion[multiIndex],
                            question: {
                                ...value,
                                isNps: R.pathOr(false, ['settings', 'frontend', 'renderNPS'], value)
                                            || (R.pathOr(false, ['metadata', 'kpiType'], value) === 'NPS'),
                            },
                            questionId: value.value,
                            parentQuestion: value.parentId,
                            questionTypeCode: value.questionTypeCode,
                            value: value.value,
                            name: multiIndex + 1,
                        },
                        multiIndex,
                    ),
                    field: 'multiQuestion',
                },
                { value: 'other', field: 'widgetBasis' },
            ]
        } else if (widgetType === 'single') {
            newValues = [
                { value, field: 'question' },
                { value: value.value, field: 'questionId' },
                { value: value.parentId, field: 'parentQuestion' },
                { value: value.questionTypeCode, field: 'questionTypeCode' },
                { value: 'question', field: 'widgetBasis' },
                { value: '', field: 'displayAs' },
            ]
        } else if (['quadrantAnalysis'].includes(widgetType)) {
            const axis = this.quadrantIndex[multiIndex]
            const { quadrant } = newWidget
            const metricObject = this.getMetricObject()
            newValues = [
                { value: 'other', field: 'widgetBasis' },
                {
                    value: {
                        ...quadrant,
                        [axis]: {
                            ...metricObject,
                            question: value,
                            value: value.value,
                            group: value.group,
                            questionId: value.value,
                            parentQuestion: value.parentId,
                            questionTypeCode: value.questionTypeCode,

                            displayAs: '',
                        },
                    },
                    field: 'quadrant',
                },
            ]
        }

        changeNewWidgetBulk(newValues)
        this.setIsNumeric(value)
        if (widgetType === 'single') {
            this.blurDisplayAs({ name: this.getDisplayAsOptions()[0].value, value: true })
        } else {
            this.setOptionFilters()
        }
    }

    getDisplayAsOptions = () => {
        const { dashboardWizardStore } = this.props
        const { newWidget } = dashboardWizardStore
        const { questionTypeCode, isNumeric } = newWidget

        return R.pathOr(
            [],
            ['questionDisplayAs', questionTypeCode, isNumeric],
            dashboardWizardStore,
        )
    }

    blurDisplayAs = (v) => {
        const { dashboardWizardStore } = this.props
        dashboardWizardStore.setDisplayAs(v)
        this.setOptionFilters()
    }

    getQuestionControlOptions = () => {
        const { dashboardWizardStore } = this.props
        const {
            widgetType, detailedQuestions, multiDetailedQuestions, getMultiQuadrantQuestions,
        } = dashboardWizardStore
        const { multiIndex } = this.state

        if (widgetType === 'single') return detailedQuestions
        if (widgetType === 'multi') return multiDetailedQuestions.get(multiIndex) || []
        if (widgetType === 'quadrantAnalysis') return getMultiQuadrantQuestions(false, false, multiIndex, this.quadrantIndex)
        return []
    }

    renderMetricControls = () => {
        const { dashboardWizardStore } = this.props
        const {
            newWidget,
            changeNewWidget,
            changeNewWidgetBulk,
            detailedQuestions,
            multiDetailedQuestions,
            loadingQuestions,
            widgetType,
            getAllowedKpis,
            registrationDataQuestions,
        } = dashboardWizardStore

        const metricObject = this.getMetricObject()
        const isMulti = ['multi', 'quadrantAnalysis'].includes(widgetType)
        const { multiIndex } = this.state
        const kpiOptions = widgetType === 'aggregated' && newWidget.respondentType ? getAllowedKpis()
            .filter((k) => (
                k.respondentType === R.pathOr(newWidget.respondentType, ['respondentType', 'value'], newWidget)
                && k.industry === R.pathOr(newWidget.industry, ['industry', 'value'], newWidget)
                && k.surveyType === R.pathOr(newWidget.surveyType, ['surveyType', 'value'], newWidget)
                && k.productType === R.pathOr(newWidget.productType, ['productType', 'value'], newWidget)
            )).map((k) => (
                {
                    label: k.name,
                    value: k.kpiId,
                    isMatrixKpi: !!k.settings.quadrantNames,
                    matrixAxisNames: k.settings.quadrantNames ? Object.fromEntries(k.variables.map(({ name }) => name.split('-'))) : undefined,
                }
            )) : []

        const industries = this.getIndustryOptions()
        const productTypes = this.getProductTypeOptions()
        const surveyTypes = this.getSurveyTypeOptions()
        const respondentTypes = this.getRespondentTypeOptions()
        const displayAsOptions = this.getDisplayAsOptions()
        const questionOptions = this.getQuestionControlOptions()
        return (
            <div className="controls">
                <Select
                    key={`industry ${multiIndex}`}
                    label="industry"
                    name="industry"
                    options={industries}
                    onBlur={this.blurIndustry}
                    value={industries.find((i) => i.value === metricObject.industry)}
                    showDisabled={!isMulti && industries.length < 2}
                />
                <Select
                    key={`productType ${multiIndex}`}
                    label="Event Type"
                    name="productType"
                    options={productTypes}
                    onBlur={this.blurProductType}
                    value={productTypes.find((p) => p.value === metricObject.productType)}
                    showDisabled={!isMulti && productTypes.length < 2}
                />
                <Select
                    key={`surveyType ${multiIndex}`}
                    label="Survey Type"
                    name="surveyType"
                    options={surveyTypes}
                    onBlur={this.blurSurveyType}
                    value={surveyTypes.find((s) => s.value === metricObject.surveyType)}
                    showDisabled={!isMulti && surveyTypes.length < 2}
                />
                <Select
                    key={`respondentType ${multiIndex}`}
                    label="Respondent Type"
                    name="respondentType"
                    options={respondentTypes}
                    onBlur={this.blurRespondentTypes}
                    value={respondentTypes.find((r) => r.value === metricObject.respondentType)}
                    showDisabled={!isMulti && respondentTypes.length < 2}
                />
                {['single', 'multi', 'quadrantAnalysis'].includes(widgetType) ? (
                    <Select
                        key={`question ${multiIndex}`}
                        label="Question"
                        name="question"
                        options={questionOptions}
                        onBlur={this.blurQuestion}
                        value={questionOptions.find((q) => q.value === metricObject.question?.value)}
                        showDisabled={(!isMulti && !metricObject.respondentType) || loadingQuestions}
                        isLoading={loadingQuestions}
                    />
                ) : null }
                {['aggregated'].includes(widgetType) ? (
                    <Select
                        key={`kpi ${multiIndex}`}
                        label="Kpi"
                        name="kpi"
                        options={kpiOptions}
                        onBlur={(value) => {
                            // changeNonWidgetReset(kpi)
                            changeNewWidget('other', 'widgetBasis')
                            changeNewWidget('KPI', 'nonQuestionType')
                            changeNewWidget(value.value, 'nonQuestionKpiType')
                            changeNewWidget(value.isMatrixKpi, 'isMatrixKpi')
                            changeNewWidget({ ...newWidget.widgetRenderSettings, matrixAxisNames: value.matrixAxisNames }, 'widgetRenderSettings')
                        }}
                        value={newWidget.respondentType && kpiOptions.length
                            ? kpiOptions.find(({ value }) => value === newWidget.nonQuestionKpiType)
                            : ''}
                        showDisabled={(!isMulti && !newWidget.respondentType) || loadingQuestions}
                        isLoading={loadingQuestions}
                    />
                ) : null }
                {widgetType === 'single' ? (
                    <CheckboxGroup
                        key={`displayAs ${multiIndex}`}
                        cssClass="formControl"
                        label={(
                            <div className="selectLabel">
                                {i18n.t('dynamicDash.wizard.displayAsLabel')}
                                <i className="fa-solid fa-circle-question">
                                    <Tooltip text={i18n.t('dynamicDash.wizard.displayAsTooltip')} direction="bottom" />
                                </i>
                            </div>
                        )}
                        options={displayAsOptions}
                        defaultChecked={R.pathOr([], ['newWidget', 'displayAs'], dashboardWizardStore)}
                        refreshOnRender
                        radio
                        name="displayAs"
                        isMandatory
                        radioValue={R.pathOr('', ['newWidget', 'displayAs'], dashboardWizardStore)}
                        onBlur={this.blurDisplayAs}
                    />
                ) : null }
                {['single', 'multi'].includes(widgetType)
                && registrationDataQuestions.includes(R.pathOr(undefined, ['question', 'value'], metricObject)) ? (
                    <Checkbox
                            key={`includeRegistrationData ${multiIndex}`}
                            cssClass="formControl"
                            label="Include Registration data"
                            defaultChecked={R.pathOr(false, ['newWidget', 'includeRegistrationData'], dashboardWizardStore)}
                            name="includeRegistrationData"
                            onBlur={({ value }) => changeNewWidget(value, 'includeRegistrationData')}
                        />
                    ) : null }
            </div>
        )
    }

    changeDataLevel = (value, field) => {
        const { dashboardWizardStore } = this.props
        const { changeNewWidget } = dashboardWizardStore
        dashboardWizardStore.createTreeData(
            value,
            R.pathOr([], ['newWidget', 'filter'], dashboardWizardStore),
        )
        changeNewWidget(value, field)
    }

    getTimePeriodOptions = () => {
        const { dashboardWizardStore } = this.props
        const { dashStore, newWidget } = dashboardWizardStore
        const { oId } = dashStore

        return oId === 6 ? [
            { value: 'YOY', label: 'Year On Year' },
        ]
            : R.pathOr([], ['timePeriodOptions', newWidget.dataLevel], dashboardWizardStore)
    }

    renderWidgetScope = () => {
        const { dashboardWizardStore } = this.props
        const {
            changeNewWidget,
            newWidget,
        } = dashboardWizardStore

        const { activePanel } = this.state

        return (
            <Panel style={{ overflow: 'visible' }} cssClass={`widgetScope ${activePanel === 'widgetScope' ? '' : 'collapsed'}`}>
                <div
                    className="collapseIcon"
                    onClick={() => this.toggleCollapse(activePanel === 'widgetScope' ? undefined : 'widgetScope')}
                >
                    <div
                        className="collapseIconContainer"
                    >
                        {
                            activePanel === 'widgetScope'
                                ? <i className="fa-regular fa-chevron-left" />
                                : <i className="fa-regular fa-chevron-right" />
                        }
                    </div>
                </div>
                <div className="collapsedMenuLabel" style={{ display: activePanel !== 'widgetScope' ? 'block' : 'none' }}>
                    Widget Scope
                </div>
                <div className="controls">
                    <div className="title">
                        <h2>Widget Scope</h2>
                    </div>
                    <Select
                        label={i18n.t('dynamicDash.wizard.dataLevelLabel')}
                        name="dataLevel"
                        options={R.pathOr([], ['dataLevels'], dashboardWizardStore)}
                        onBlur={({ value }) => {
                            this.changeDataLevel(value, 'dataLevel')
                        }}
                        isMandatory
                        value={R.pathOr([], ['dataLevels'], dashboardWizardStore).find(({ value }) => value === newWidget.dataLevel)}
                    />
                    <Select
                        label={i18n.t('dynamicDash.wizard.timePeriodLabel')}
                        name="timePeriod"
                        options={this.getTimePeriodOptions()}
                        onBlur={({ value }) => {
                            changeNewWidget(value, 'timePeriod')
                        }}
                        isMandatory
                        value={this.getTimePeriodOptions().find(({ value }) => value === newWidget.timePeriod)}
                    />
                    <StagedForm
                        store={dashboardWizardStore}
                        controls={this.widgetScopeControls().filter((c) => !!c)}
                        valueHolder={dashboardWizardStore.newWidget}
                    />
                </div>
            </Panel>
        )
    }

    metricPanel = (controls) => {
        const { activePanel } = this.state
        return (
            <Panel style={{ overflow: 'visible' }} cssClass={`metrics ${activePanel === 'metrics' ? '' : 'collapsed'}`}>
                <div
                    className="collapseIcon"
                    onClick={() => this.toggleCollapse(activePanel === 'metrics' ? undefined : 'metrics')}
                >
                    <div
                        className="collapseIconContainer"
                    >
                        {
                            activePanel === 'metrics'
                                ? <i className="fa-regular fa-chevron-left" />
                                : <i className="fa-regular fa-chevron-right" />
                        }
                    </div>
                </div>
                <div
                    className="collapsedMenuLabel"
                    style={{ display: activePanel !== 'metrics' ? 'block' : 'none' }}
                >
                    Metrics
                </div>
                <div className="panelBody">
                    {controls}
                </div>
            </Panel>
        )
    }

    renderMetricSelector = () => {
        const { dashboardWizardStore } = this.props
        const { newWidget, changeNewWidget, widgetType } = dashboardWizardStore
        const { multiQuestion } = newWidget
        const { multiIndex } = this.state
        const metrics = widgetType === 'multi' ? multiQuestion : ['X Axis', 'Y Axis', 'Z Axis']
        const metricTabs = metrics.map((m, i) => (
            <div
                onClick={() => this.setState({ multiIndex: i })}
                className={`metric ${multiIndex === i ? 'active' : ''}`}
            >
                {widgetType === 'multi' ? `Metric ${i + 1}` : m}
            </div>
        ))

        if (widgetType === 'multi' && multiQuestion.length < 6) {
            metricTabs.push(
                <div
                    onClick={() => changeNewWidget(this.newMultiQuestionValue({}, multiQuestion.length), 'multiQuestion')}
                    className="metric addMetric"
                >
                    Add metric...
                </div>,
            )
        }
        return (
            <div className="metricSelector">
                {metricTabs}
            </div>
        )
    }

    renderSingleMetricSettings = () => {
        const { dashboardWizardStore, widgetKey } = this.props
        const { newWidget } = dashboardWizardStore
        const { loading } = newWidget
        return (
            <div className="singleMetric">
                {this.metricPanel(
                    <>
                        <div className="title">
                            <h2>Metrics</h2>
                        </div>
                        {this.renderMetricControls()}
                    </>,
                )}
                {this.renderWidgetScope()}
                <ChartPreviewContainer loading={loading} dashboardWizardStore={dashboardWizardStore} editing={!!widgetKey} />
            </div>
        )
    }

    renderMultiMetricSettings = () => {
        const { dashboardWizardStore, widgetKey } = this.props
        const { newWidget } = dashboardWizardStore
        const { loading } = newWidget
        return (
            <div className="singleMetric">
                {this.metricPanel(
                    <>
                        <div className="title">
                            <h2>Metrics</h2>
                        </div>
                        {this.renderMetricSelector()}
                        {this.renderMetricControls()}
                    </>,
                )}
                {this.renderWidgetScope()}
                <ChartPreviewContainer loading={loading} dashboardWizardStore={dashboardWizardStore} editing={!!widgetKey} />
            </div>
        )
    }

    renderMetricSettings = () => {
        const { dashboardWizardStore } = this.props
        const { widgetType } = dashboardWizardStore
        switch (widgetType) {
            case 'single':
            case 'aggregated':
                return this.renderSingleMetricSettings()
            case 'multi':
                return this.renderMultiMetricSettings()
            case 'quadrantAnalysis':
                return this.renderMultiMetricSettings()
            default:
                return null
        }
    }

    closeShowWidgetCreator = () => {
        const { dashboardWizardStore } = this.props
        const { dashStore, resetStore } = dashboardWizardStore
        const { toggleShowWidgetCreator } = dashStore
        resetStore()
        toggleShowWidgetCreator()
    }

    updateLayout = (newKey) => {
        const { dashboardWizardStore, widgetKey } = this.props
        const { dashStore } = dashboardWizardStore
        const { layouts } = dashStore
        return Object.keys(layouts).reduce((layout, k) => (
            {
                ...layout,
                [k]: layouts[k]
                    .map((l) => ({ ...l, i: l.i === widgetKey ? newKey : l.i })),
            }
        ), {})
    }

    updateWidget = () => {
        const { dashboardWizardStore, widgetKey } = this.props
        const { dashStore } = dashboardWizardStore
        const { updateWidget, updateLayouts, saveDashboard } = dashStore
        const widgets = R.pathOr([{}], ['widgets'], dashStore)
        const widgetIndex = widgets.findIndex((w) => w.key === widgetKey)
        const newKey = uuidv4()
        const newLayout = this.updateLayout(newKey)
        const widget = dashboardWizardStore.addWidget(true)
        widget.key = newKey
        updateWidget(widgetIndex, widget)
        updateLayouts(newLayout)
        saveDashboard()
    }

    addWidget = () => {
        const { dashboardWizardStore, widgetKey } = this.props
        const { newWidget, addWidget } = dashboardWizardStore
        const { error } = this.state
        if (!newWidget.chart) {
            this.setState({ error: { ...error, chart: true } })
            return
        }
        if (widgetKey) {
            this.updateWidget()
        } else {
            addWidget()
        }
        this.closeShowWidgetCreator()
    }

    render() {
        const { dashboardWizardStore, widgetKey } = this.props
        const { dashStore } = dashboardWizardStore
        const { showWidgetCreator } = dashStore
        return (
            <Modal
                show={showWidgetCreator}
                onClose={this.closeShowWidgetCreator}
                cssClass="widgetCreatorModal dashboardCreationWizard advancedPreview"
            >
                <div className="widgetCreatorContainer">
                    <ModalHeader cssClass="header">
                        <div className="title">
                            Add new widget
                        </div>
                    </ModalHeader>
                    <div className="body">
                        {this.renderMetricSettings()}
                    </div>
                    <ModalFooter>
                        <Button
                            label="Cancel"
                            cssClass="tertiary regular"
                            style={{ marginRight: '25px', width: '134px' }}
                            onClick={this.closeShowWidgetCreator}
                        />
                        <Button
                            label={widgetKey ? 'Update Widget' : 'Add Widget'}
                            cssClass="primary regular"
                            style={{ width: '134px' }}
                            onClick={this.addWidget}
                        />
                    </ModalFooter>
                </div>
            </Modal>
        )
    }
}

export default WidgetCreator
